<template>
  <div
    class="tornado-chart"
    :class="{ 'is-static': isStatic, 'is-horizontal': isHorizontal }"
  >
    <DDV
      v-slot="{ margins, onUpdateNearestPoint, enableMouseTracking }"
      v-bind="$attrs"
      class="pump-tornado"
      @update:dom-state="domState = $event"
    >
      <template
        v-if="domState && domState.plotWidth > 0 && domState.plotHeight > 0"
      >
        <rect
          class="plotBg"
          :x="margins.left"
          :y="margins.top"
          :width="domState?.plotWidth"
          :height="domState?.plotHeight"
        />

        <g v-if="readyToDraw">
          <Axis
            :label="xAxisLabel"
            :scaler="xScaler"
            :domain="paddedDomainX"
            :plot-margin-px="6"
            :x="margins.left"
            :y="margins.top"
            :tick-frequency="ddvTickFrequencies?.x"
            draw-grid-lines
          />
          <Axis
            :label="yAxisLabel"
            :scaler="yScaler"
            :domain="paddedDomainY"
            :plot-margin-px="6"
            :x="margins.left"
            :y="margins.top"
            :tick-frequency="ddvTickFrequencies?.y"
            is-vertical
            draw-grid-lines
          />
          <g
            v-if="(domState.plotWidth || 0) > 0"
            :transform="`translate(${margins.left} ${margins.top})`"
          >
            <!-- Frequency HZ Datasets -->
            <BSP
              v-for="(ds, i) in validFrequencyDataSets"
              v-slot="{ screenPoints }"
              :key="`frequency-plot-${i}`"
              :points="ds.points"
              :screen-points="
                ds.points.map(p => ({
                  x: xScaler.scale(p.x),
                  y: yScaler.scale(p.y)
                }))
              "
            >
              <text
                class="frequency-text"
                :x="screenPoints.at(2)?.x"
                :y="screenPoints.at(2)?.y"
                :dy="10"
              >
                {{ ds.label }}
              </text>
              <LinePlot
                class="frequency-line"
                :data-series-number="i + 1"
                :x="margins.left"
                :y="margins.top"
                :style="{
                  strokeDasharray:
                    100 ** ((validFrequencyDataSets.length - i) / 10)
                }"
                :screen-points="screenPoints"
                curve-interpolator="catmull-rom"
              />
            </BSP>

            <!-- ROR Datasets -->
            <BSP
              v-for="(ds, i) in validRorDataSets"
              v-slot="{ nearestPoint, screenPoints }"
              :key="`ror-plot-${i}`"
              :points="ds.points"
              :screen-points="
                ds.points.map(p => ({
                  x: xScaler.scale(p.x),
                  y: yScaler.scale(p.y)
                }))
              "
              @update:nearest-point="
                enableMouseTracking &&
                  onUpdateNearestPoint(
                    ds.title ?? '',
                    i + 1,
                    true,
                    $event,
                    formatValue,
                    formatValue,
                    ds.xAxisLabel,
                    ds.yAxisLabel
                  )
              "
            >
              <g class="ror-plot" :style="`--rorPlotColor: ${ds.color}`">
                <text class="ror-text" dy="-2">
                  <textPath startOffset="30%" :href="`#${chartId}-${ds.title}`">
                    {{ ds.title }}
                  </textPath>
                </text>
                <LinePlot
                  :id="`${chartId}-${ds.title}`"
                  class="ror-line"
                  :x="margins.left"
                  :y="margins.top"
                  :screen-points="screenPoints"
                  :curve-interpolator="
                    screenPoints.length > 3 ? 'catmull-rom' : 'straight'
                  "
                />

                <NearestPoint
                  v-if="nearestPoint?.node && !isStatic"
                  class="ror-point"
                  :data-series-number="i + 1"
                  :nearest-point="nearestPoint"
                />
              </g>
            </BSP>

            <BSP
              v-for="(ds, i) in validAdditionalDataSets"
              v-slot="{ nearestPoint, screenPoints }"
              :key="`additional-plot-${i}`"
              :points="ds.points"
              :screen-points="
                ds.points.map(p => ({
                  x: xScaler.scale(p.x),
                  y: yScaler.scale(p.y)
                }))
              "
              @update:nearest-point="
                enableMouseTracking &&
                  onUpdateNearestPoint(
                    ds.title ?? '',
                    0,
                    true,
                    $event,
                    formatValue,
                    formatValue,
                    ds.xAxisLabel,
                    ds.yAxisLabel
                  )
              "
            >
              <g
                :class="`additional-plot`"
                :style="`--additionalPlotColor: ${ds.color}`"
              >
                <LinePlot
                  class="additional-line"
                  :data-series-number="0"
                  :curve-interpolator="ds.curveType ?? 'catmull-rom'"
                  :x="margins.left"
                  :y="margins.top"
                  :screen-points="screenPoints"
                />

                <NearestPoint
                  v-if="nearestPoint?.node && enableMouseTracking"
                  class="additional-point"
                  :data-series-number="0"
                  :nearest-point="nearestPoint"
                />
              </g>
            </BSP>

            <g
              v-for="(p, i) in operatingPoints"
              :key="p.label"
              :transform="`
              translate(
                ${xScaler.scale(p.point.x)},
                ${yScaler.scale(p.point.y)}
              )`"
            >
              <g v-if="activeItems[i]">
                <circle
                  :cx="0"
                  :cy="0"
                  :r="5"
                  :stroke="p.color"
                  :stroke-width="2"
                  fill="transparent"
                />
                <circle
                  :cx="0"
                  :cy="0"
                  :r="5"
                  :stroke="p.color"
                  :stroke-opacity="0.3"
                  :stroke-width="8"
                  fill="transparent"
                />
                <circle :cx="0" :cy="0" :r="4" fill="var(--plotSurface)" />
              </g>
            </g>
          </g>

          <ChartTitle v-if="title" :title="title" />

          <Crosshairs
            v-if="enableMouseTracking && readyToDraw"
            :x="margins.left"
            :y="margins.top"
          />
        </g>
        <g v-else>
          <text
            fill="var(--axisLabelColor)"
            :x="(domState?.plotWidth || 0) / 2"
            :y="(domState?.plotHeight || 0) / 2"
            text-anchor="middle"
          >
            No Data
          </text>
        </g>
      </template>
    </DDV>
    <CustomLegend
      v-if="drawLegend"
      v-model="activeItems"
      :static="isStatic"
      :horizontal="isHorizontal"
      :model="operatingPoints"
      :x-label="xLegendLabel"
      :y-label="yLegendLabel"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, toRefs } from "vue";
import type { DataSet, DOMState } from "@incytestudios/ddv/types/common";
import {
  DDV,
  BSP,
  LinePlot,
  NearestPoint,
  Crosshairs,
  ChartTitle,
  Axis,
  useAggregateDomains,
  useLinearScaler,
  isNumberDomain,
  fromRange,
  type NumericDomain,
  getDefaultValueFormatter
} from "@incytestudios/ddv";
import type { ConvertableUnit } from "@/libs/units";
import type { LabelledDataSet, OperatingPoint } from "@/components/types";
import CustomLegend from "./CustomLegend.vue";
import { useComputedDomains } from "@/composables/useComputedDomains";
import { useOperatingPointsInAggregatedDataSets } from "@/composables/useOperatingPointsInAggregatedDataSets";
const props = defineProps<{
  computeDomainMinY?: boolean;
  computeDomainMaxY?: boolean;
  computeDomainMaxX?: boolean;
  computeDomainMinX?: boolean;
  computedDomainYBufferMultiplier?: number;
  computedDomainXBufferMultiplier?: number;
  log?: boolean;
  title?: string;
  drawLegend?: boolean;
  isStatic?: boolean;
  isHorizontal?: boolean;
  minHeight?: number;
  reverseX?: boolean;
  reverseY?: boolean;
  xLabel?: string;
  yLabel?: string;
  frequencyDataSets: LabelledDataSet[];
  rorDataSets: DataSet[];
  additionalDataSets: DataSet[];
  operatingPoints?: OperatingPoint[];
  xUnit?: ConvertableUnit;
  yUnit?: ConvertableUnit;
  xLegendLabel?: string;
  yLegendLabel?: string;
}>();

defineEmits<{
  (e: "update:x-unit", value: ConvertableUnit): void;
  (e: "update:y-unit", value: ConvertableUnit): void;
}>();

const {
  operatingPoints,
  frequencyDataSets,
  rorDataSets,
  additionalDataSets,
  drawLegend
} = toRefs(props);

const validRorDataSets = computed(() =>
  rorDataSets.value.filter(dataSet => dataSet.points.length > 0)
);

const validFrequencyDataSets = computed(() =>
  frequencyDataSets.value.filter(dataSet => dataSet.points.length > 0)
);

const validAdditionalDataSets = computed(() =>
  additionalDataSets.value.filter(dataSet => dataSet.points.length > 0)
);

const formatValue = getDefaultValueFormatter();

const ddvTickFrequencies = inject<{ x: string; y: string }>(
  "ddvTickFrequencies"
);

// inSituLabel hardcoded here since we know this is used for tornado charts
// and flow rate along the x axis
const xAxisLabel = computed(() => {
  if (props.xUnit) {
    return `${props.xLabel || "X"} (${
      props.xUnit?.inSituLabel || props.xUnit?.shortLabel || props.xUnit.label
    })`;
  } else {
    return props.xLabel || "X";
  }
});

const yAxisLabel = computed(() => {
  if (props.yUnit) {
    return `${props.yLabel || "Y"} (${
      props.yUnit?.shortLabel || props.yUnit.label
    })`;
  } else {
    return props.yLabel || "Y";
  }
});

const chartId = ref(Math.round(Math.random() * 10000));

const readyToDraw = computed(() =>
  validFrequencyDataSets.value.some(dataSet => dataSet.points.length)
);

// #region Chart UX
const activeItems = drawLegend?.value
  ? ref([...(operatingPoints?.value || []).map(() => true)])
  : computed(() => [...(operatingPoints?.value || []).map(() => true)]);

const domState = ref<DOMState>();

const tableDataSets = computed(() => [
  ...(validAdditionalDataSets?.value ?? []),
  ...validFrequencyDataSets.value,
  ...validRorDataSets.value
]);

const absoluteScale = ref(true);

const inactiveItems = ref([]);

const aggregatedDataSets = useOperatingPointsInAggregatedDataSets({
  operatingPoints,
  tableDataSets
});

const { aggregateX, aggregateY } = useAggregateDomains(
  aggregatedDataSets,
  inactiveItems,
  absoluteScale
);

const computedDomains = useComputedDomains(
  aggregatedDataSets,
  props.computedDomainXBufferMultiplier,
  props.computedDomainYBufferMultiplier
);

const reverseX = computed(() => !!props.reverseX);
const reverseY = computed(() => !!props.reverseY);

const targetX = computed<NumericDomain>(() =>
  fromRange(domState.value?.plotWidth ?? 0)
);
const targetY = computed<NumericDomain>(() =>
  fromRange(domState.value?.plotHeight ?? 0)
);

const paddedDomainX = computed<NumericDomain>(() => {
  let min = 0;
  let max = 0;
  if (props.computeDomainMinX) {
    min = computedDomains.value.minX;
  } else if (isNumberDomain(aggregateX.value)) {
    min = aggregateX.value.min as number;
  }

  if (props.computeDomainMaxX) {
    max = computedDomains.value.maxX;
  } else if (isNumberDomain(aggregateX.value)) {
    max = aggregateX.value.max as number;
  }
  return { min, max };
});

const paddedDomainY = computed<NumericDomain>(() => {
  let min = 0;
  let max = 0;
  if (props.computeDomainMinY) {
    min = computedDomains.value.minY;
  } else if (isNumberDomain(aggregateY.value)) {
    min = aggregateY.value.min;
  }

  if (props.computeDomainMaxY) {
    max = computedDomains.value.maxY;
  } else if (isNumberDomain(aggregateY.value)) {
    max = aggregateY.value.max * 1.1;
  }
  return { min, max };
});

const xScaler = useLinearScaler(paddedDomainX, targetX, reverseX);
const yScaler = useLinearScaler(paddedDomainY, targetY, reverseY);

// #endregion Chart UX
</script>

<style lang="scss" scoped>
.tornado-chart {
  display: grid;
  grid-template-columns: 3fr max-content;
  grid-template-rows: 1fr;

  &.is-horizontal {
    grid-template-columns: 1fr;
    grid-template-rows: min-content min-content;
  }

  &.is-static {
    :deep(.ddv) {
      &.isPlotHovered {
        cursor: auto !important;
      }
    }
  }
}
.pump-tornado {
  --rorBepColor: hsla(248, 50%, 60%, 1);

  :deep(.ror-plot) {
    .ror-line {
      transition: none;
      stroke: var(--rorPlotColor);
    }

    .ror-text {
      fill: var(--rorPlotColor);
      font-family: var(--axisTickFontFamily);
      font-weight: var(--axisTickFontWeight);
    }

    .ror-point {
      --activePlotColor: var(--rorPlotColor);
    }
  }

  :deep(.additional-plot) {
    .additional-line {
      transition: none;
      stroke: var(--additionalPlotColor);
    }

    .additional-point {
      --activePlotColor: var(--additionalPlotColor);
    }
  }

  :deep(.frequency-point) {
    --activePlotColor: var(--gray-500);
  }

  :deep(.frequency-line) {
    --activePlotColor: var(--gray-500);
  }

  :deep(.frequency-text) {
    opacity: 0.5;
    fill: var(--gray-600);
    font-family: var(--axisTickFontFamily);
    font-size: var(--axisTickFontSize);
    font-weight: var(--axisTickFontWeight);
  }
}

.limited-text {
  font-size: small;
  font-style: italic;
  line-height: 1.1;
  margin-top: 0.5em;
}
</style>
